export function editorValuesEqual(a: unknown, b: unknown): boolean {
  if (Object.is(a, b)) return true;
  if (typeof a === 'number' && typeof b === 'number') return Number.isFinite(a) && Number.isFinite(b) && Math.abs(a - b) < 1e-7;
  if (!a || !b || typeof a !== 'object' || typeof b !== 'object') return false;
  if (Array.isArray(a) !== Array.isArray(b)) return false;
  const keys = Object.keys(a).sort();
  const other = Object.keys(b).sort();
  return keys.length === other.length && keys.every((key, i) => key === other[i] && editorValuesEqual(a[key], b[key]));
}

/** Only a stationary primary-pointer click that starts AND ends on the backdrop. */
export class BackdropClickTracker {
  private start: { id: number; x: number; y: number } | null = null;
  down(event: { pointerId: number; clientX: number; clientY: number; button: number; isPrimary: boolean }, onBackdrop: boolean): void {
    this.start = onBackdrop && event.button === 0 && event.isPrimary
      ? { id: event.pointerId, x: event.clientX, y: event.clientY } : null;
  }
  move(event: { pointerId: number; clientX: number; clientY: number }): void {
    if (this.start?.id === event.pointerId && Math.hypot(event.clientX - this.start.x, event.clientY - this.start.y) > 5) this.cancel();
  }
  up(event: { pointerId: number; clientX: number; clientY: number }, onBackdrop: boolean): boolean {
    this.move(event);
    const click = this.start?.id === event.pointerId && onBackdrop;
    this.cancel();
    return click;
  }
  cancel(): void { this.start = null; }
}

interface EditorDismissOptions {
  hasChanges: () => boolean;
  onClose: (save: boolean) => void;
  beforePrompt?: () => void;
  onKeepEditing?: () => void;
}

let nextDialogId = 0;

export class EditorDismissGuard {
  private readonly lifetime = new AbortController();
  private readonly pointer = new BackdropClickTracker();
  private dialog: HTMLDivElement | null = null;
  private restoreFocus: HTMLElement | null = null;
  private panel: HTMLElement | null = null;
  private panelWasInert = false;

  constructor(private readonly root: HTMLElement, private readonly options: EditorDismissOptions) {
    const signal = this.lifetime.signal;
    root.addEventListener('pointerdown', event => this.pointer.down(event, event.target === root), { capture: true, signal });
    window.addEventListener('pointermove', event => this.pointer.move(event), { capture: true, signal });
    root.addEventListener('pointerup', event => {
      if (this.pointer.up(event, event.target === root)) this.requestClose();
    }, { capture: true, signal });
    window.addEventListener('pointercancel', () => this.pointer.cancel(), { signal });
    window.addEventListener('blur', () => this.pointer.cancel(), { signal });
  }

  requestClose(): void {
    if (this.lifetime.signal.aborted || this.dialog || !this.root.isConnected) return;
    if (!this.options.hasChanges()) { this.options.onClose(false); return; }
    this.options.beforePrompt?.();
    // A canceled in-progress gesture may have restored the original state.
    if (!this.options.hasChanges()) { this.options.onClose(false); return; }
    this.restoreFocus = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    this.panel = this.root.querySelector<HTMLElement>('.custom-editor-modal');
    if (this.panel) { this.panelWasInert = this.panel.inert; this.panel.inert = true; }
    const id = `editor-unsaved-${++nextDialogId}`;
    const overlay = document.createElement('div');
    overlay.className = 'editor-unsaved-backdrop';
    overlay.innerHTML = `<div class="editor-unsaved-dialog" role="alertdialog" aria-modal="true" aria-labelledby="${id}-title" aria-describedby="${id}-description">
      <h3 id="${id}-title">Save changes?</h3>
      <p id="${id}-description">You have unsaved changes in this editor.</p>
      <div class="editor-unsaved-actions">
        <button type="button" data-editor-decision="keep">Keep editing</button>
        <button type="button" data-editor-decision="discard">Discard</button>
        <button type="button" class="primary" data-editor-decision="save">Save changes</button>
      </div>
    </div>`;
    this.dialog = overlay;
    overlay.addEventListener('pointerdown', event => event.stopPropagation());
    overlay.addEventListener('click', event => {
      event.stopPropagation();
      const decision = (event.target as Element).closest<HTMLElement>('[data-editor-decision]')?.dataset.editorDecision;
      if (!decision) return;
      this.hidePrompt(decision === 'keep');
      if (decision !== 'keep') this.options.onClose(decision === 'save');
      else this.options.onKeepEditing?.();
    });
    overlay.addEventListener('keydown', event => {
      event.stopPropagation();
      if (event.key === 'Escape') { event.preventDefault(); this.hidePrompt(true); this.options.onKeepEditing?.(); return; }
      if (event.key === 'Tab') {
        const buttons = Array.from(overlay.querySelectorAll<HTMLButtonElement>('button'));
        const first = buttons[0], last = buttons[buttons.length - 1];
        if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
        else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
      }
    });
    this.root.appendChild(overlay);
    overlay.querySelector<HTMLButtonElement>('[data-editor-decision="keep"]')?.focus({ preventScroll: true });
  }

  dispose(): void {
    this.lifetime.abort();
    this.pointer.cancel();
    this.hidePrompt(false);
  }

  private hidePrompt(focus: boolean): void {
    this.dialog?.remove();
    this.dialog = null;
    if (this.panel) this.panel.inert = this.panelWasInert;
    this.panel = null;
    if (focus && this.restoreFocus?.isConnected) this.restoreFocus.focus({ preventScroll: true });
    this.restoreFocus = null;
  }
}
